<script setup lang="ts">
import { ref, computed } from 'vue';
import { useInstitutionStore } from '@/stores/institution';
import { capitalize } from 'lodash';

const institutionStore = useInstitutionStore();
const emit = defineEmits(['onChange']);

interface Props {
  keyboardClass?: string;
  placeholder?: string;
  title?: string;
}

withDefaults(defineProps<Props>(), {
  keyboardClass: 'keyboard-simple',
  placeholder: 'global.description',
});

const input = ref<string>('');
const lastKeyPress = ref<string>('');
const upperCase = ref<boolean>(true);
const inputRef = ref<HTMLInputElement>();
const pressedKey = ref<string | null>(null);

const locale = computed(() =>
  institutionStore?.locale === 'de' ? 'de' : 'da'
);

const NUMBERS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'];

const KEYBOARD_LAYOUTS: Record<string, string[][]> = {
  de: [
    [...NUMBERS, 'ß'],
    ['q', 'w', 'e', 'r', 't', 'z', 'u', 'i', 'o', 'p', 'ü'],
    ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l', 'ö', 'ä'],
    ['shift', 'y', 'x', 'c', 'v', 'b', 'n', 'm', 'bksp'],
    ['@', 'space', '?'],
  ],
  da: [
    NUMBERS,
    ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p', 'å'],
    ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l', 'æ', 'ø'],
    ['shift', 'z', 'x', 'c', 'v', 'b', 'n', 'm', 'bksp'],
    ['@', 'space', '?'],
  ],
};

const ROWS = computed(() => KEYBOARD_LAYOUTS[locale.value]);

const getSize = (key: string) => {
  let width = 68;

  if (key === 'shift' || key === 'bksp') width = 106;
  if (key === 'space') width = 455;

  return {
    width: `${width}px`,
    height: '68px',
  };
};

const backspaceInterval = ref<ReturnType<typeof setInterval> | null>(null);
const backspaceDelay = 100;

function deleteCharacter() {
  if (!inputRef.value) return;

  const cursorPos = inputRef.value.selectionStart || 0;
  if (cursorPos === 0) return;

  // Delete character before cursor
  input.value =
    input.value.slice(0, cursorPos - 1) + input.value.slice(cursorPos);
  if (input.value.length === 0) upperCase.value = true;
  emit('onChange', input.value);

  // Restore cursor position
  setTimeout(() => {
    if (inputRef.value) {
      inputRef.value.setSelectionRange(cursorPos - 1, cursorPos - 1);
    }
  }, 0);
}

function startBackspaceInterval() {
  if (backspaceInterval.value === null) {
    deleteCharacter();
    backspaceInterval.value = setInterval(() => {
      deleteCharacter();
    }, backspaceDelay);
  }
}

function stopBackspaceInterval() {
  if (backspaceInterval.value !== null) {
    clearInterval(backspaceInterval.value);
    backspaceInterval.value = null;
  }
}

function focusInput(newCursorPos?: number) {
  if (inputRef.value) {
    inputRef.value.focus();
    if (newCursorPos !== undefined) {
      setTimeout(() => {
        if (inputRef.value) {
          inputRef.value.setSelectionRange(newCursorPos, newCursorPos);
        }
      }, 0);
    }
  }
}

function onPressStart(key: string, event?: MouseEvent | TouchEvent) {
  pressedKey.value = key;
}

function onPressEnd(key: string, event?: MouseEvent | TouchEvent) {
  pressedKey.value = null;

  // Don't call onKeyPress for backspace since it's handled by the interval
  if (key !== 'bksp') {
    onKeyPress(key);
  } else {
    // Just ensure focus when releasing backspace
    if (inputRef.value) {
      inputRef.value.focus();
    }
  }
}

function onTouchStart(key: string, event: TouchEvent) {
  event.preventDefault(); // Prevent mouse events from firing
  onPressStart(key, event);
  if (key === 'bksp') {
    startBackspaceInterval();
  }
}

function onTouchEnd(key: string, event: TouchEvent) {
  event.preventDefault(); // Prevent mouse events from firing
  onPressEnd(key, event);
  if (key === 'bksp') {
    stopBackspaceInterval();
  }
}

function onTouchCancel(key: string, event: TouchEvent) {
  event.preventDefault();
  pressedKey.value = null;
  if (key === 'bksp') {
    stopBackspaceInterval();
  }
}

function onKeyPress(key: string) {
  lastKeyPress.value = key;

  if (!inputRef.value) return;
  const cursorPos = inputRef.value.selectionStart || 0;

  if (key === 'shift') {
    upperCase.value = !upperCase.value;
    focusInput(cursorPos);
    return;
  }

  if (key === 'bksp') {
    deleteCharacter();
    return;
  }

  let charToInsert = '';
  if (key === 'space') {
    charToInsert = ' ';
  } else if (key === 'ß') {
    charToInsert = upperCase.value ? 'ẞ' : 'ß';
  } else {
    charToInsert = upperCase.value ? capitalize(key) : key;
  }

  // Insert character at cursor position
  input.value =
    input.value.slice(0, cursorPos) +
    charToInsert +
    input.value.slice(cursorPos);

  if (input.value.length === 1) upperCase.value = false;

  emit('onChange', input.value);

  // Move cursor after inserted character
  focusInput(cursorPos + charToInsert.length);
}
</script>

<template>
  <div data-no-pressable>
    <p class="h800 mb-4" v-if="title">{{ title }}</p>
    <input
      ref="inputRef"
      class="w-full input border-b-2 border-darkNeutral-600A mb-12 text-6xl pb-2 font-semibold bg-neutral-100 focus:outline-none"
      :value="input"
      :placeholder="$t(placeholder)"
    />
    <div
      v-for="(row, index) in ROWS"
      :key="index"
      class="flex items-center justify-center mb-4"
    >
      <div
        v-for="(key, index) in row"
        :key="index"
        class="flex rounded-3xl border border-neutral-1000 h900 items-center justify-center select-none transition-transform duration-100 ease-out"
        :class="[
          index !== row.length - 1 ? 'mr-3' : '',
          pressedKey === key
            ? 'scale-110 bg-darkOcean-100 text-neutral-1000'
            : key === 'shift' && upperCase == true
              ? 'bg-neutral-600 text-neutral-0'
              : 'bg-neutral-0 text-neutral-1000',
        ]"
        :style="getSize(key)"
        @mousedown="onPressStart(key, $event)"
        @mouseup="onPressEnd(key, $event)"
        @touchstart="onTouchStart(key, $event)"
        @touchend="onTouchEnd(key, $event)"
        @touchcancel="onTouchCancel(key, $event)"
      >
        <fa-icon
          v-if="key === 'shift'"
          :icon="['fas', 'fa-up']"
          class="text-4xl"
        />

        <fa-icon
          v-else-if="key === 'bksp'"
          :icon="['far', 'fa-delete-left']"
          class="text-4xl"
        />

        <p v-else-if="key === 'space'">{{ key }}</p>
        <p v-else>{{ upperCase ? capitalize(key) : key }}</p>
      </div>
    </div>
  </div>
</template>

<style>
input::placeholder {
  color: rgba(21, 21, 21, 0.1);
}

input.input {
  caret-color: #151515;
}
</style>
